import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getStudentWithUUID } from "../../../services/postService.services";
import {
  FiLogOut,
  FiDownload,
  FiExternalLink,
  FiArrowRight,
  FiBookOpen,
  FiStar,
  FiBookmark,
  FiLink,
  FiFile,
  FiUser,
  FiMessageCircle,
  FiLock,
  FiMail,
  FiCheck,
} from "react-icons/fi";

// Importing images
import PurpleImage from "../../../assets/purpleImage.png";
import GreenImage from "../../../assets/GreenImage.png";
import pin from "../../../assets/pin.png";
import Document from "../../../assets/Documents.png";

// Card design templates - keeping the original gradient styles
const cardDesigns = [
  {
    name: "purple",
    background: "linear-gradient(to right, #8368FC, #9E88FF)",
    textColor: "text-white",
    textShadow: "0px 1px 2px rgba(0, 0, 0, 0.3)",
  },
  {
    name: "green",
    background: "linear-gradient(to right, #7A9757, #88FFC5)",
    textColor: "text-white",
    textShadow: "0px 1px 2px rgba(0, 0, 0, 0.3)",
  },
  {
    name: "blue",
    background: "linear-gradient(to right, #7F97C1, #A7BAEA)",
    textColor: "text-white",
    textShadow: "0px 1px 2px rgba(0, 0, 0, 0.3)",
  },
  {
    name: "gray",
    background: "linear-gradient(to right, #C0C5B9, #A6ABA9)",
    textColor: "text-white",
    textShadow: "0px 1px 2px rgba(0, 0, 0, 0.3)",
  },
];

// Helper function to get deterministic card design (not random)
const getDesignForIndex = (index) => {
  return cardDesigns[index % cardDesigns.length];
};

// Icons for different content types
const cardIcons = {
  career: <FiStar className="w-6 h-6" />,
  documents: <FiFile className="w-6 h-6" />,
  links: <FiLink className="w-6 h-6" />,
  university: <FiBookOpen className="w-6 h-6" />,
  default: <FiBookmark className="w-6 h-6" />,
};

const ProfileBuilding = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState({});
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);

  // Auth states
  const [email, setEmail] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [accessGranted, setAccessGranted] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [error, setError] = useState(null);

  // UI states
  const [activeSection, setActiveSection] = useState("all");
  const [showLogin, setShowLogin] = useState(true);

  const fetchStudent = async () => {
    try {
      const id = location.pathname.split("/")[2];
      const data = await getStudentWithUUID(id);
      setStudent(data);
      setLoading(false);
    } catch (err) {
      setError("Failed to load student data.");
      setLoading(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchStudent();
  }, []);

  // Check authentication
  useEffect(() => {
    const access = localStorage.getItem("profileAccess");
    const authToken = localStorage.getItem("authToken");
    if (access === "true" && authToken) {
      setAccessGranted(true);
    }
  }, [student.length]);

  const handleLogin = () => {
    if (!student) {
      setLoginError("Student data not loaded yet.");
      return;
    }

    const studentData = student;

    if (studentData.customer && studentData.customer.email === email) {
      if (studentData.plain_password === passwordInput) {
        localStorage.setItem("authToken", studentData.password);
        localStorage.setItem("profileAccess", "true");
        setAccessGranted(true);
      } else {
        setLoginError("Invalid password.");
      }
    } else {
      setLoginError("Invalid email.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("profileAccess");
    window.location.reload();
  };

  const handleDownloadSwot = () => {
    if (student && student.customer && student.customer.swot_test_link) {
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_BASE_URL}${student.customer.swot_test_link}`;
      link.setAttribute("download", "SWOT_Report.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert("SWOT report link not available.");
    }
  };

  // Function to parse text and convert URLs to clickable links
  const parseTextWithLinks = (text) => {
    if (!text || typeof text !== "string") return text;

    // Matches full URLs like https://..., http://..., and also www....
    const urlRegex = /((https?:\/\/)?(www\.)?[a-zA-Z0-9\-]+\.[^\s]+)/gi;

    const parts = [];
    let lastIndex = 0;

    // Use RegExp exec to find all matches and preserve surrounding text
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      // Add text before the URL
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      const rawUrl = match[0];
      const fullUrl = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;

      parts.push(
        <a
          key={match.index}
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-white underline hover:text-blue-100 transition-colors"
        >
          {rawUrl}
        </a>
      );

      lastIndex = match.index + rawUrl.length;
    }

    // Add any remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  // Function to render dynamic content cards based on domain fields
  const renderDynamicCards = () => {
    if (!student || !student?.interested_domain) return null;

    // Extract all dynamic fields from all domains
    const allDynamicFields = [];

    student.interested_domain.forEach((domain, domainIndex) => {
      if (domain.dynamic_fields) {
        Object.entries(domain.dynamic_fields).forEach(
          ([fieldName, fieldValue], fieldIndex) => {
            // Skip Documents field as it's already displayed
            if (fieldName !== "Documents") {
              allDynamicFields.push({
                domainName: domain.domain_name,
                fieldName,
                fieldValue,
                design: getDesignForIndex(domainIndex + fieldIndex),
                icon: cardIcons[fieldName.toLowerCase()] || cardIcons.default,
              });
            }
          }
        );
      }
    });

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {allDynamicFields.map((field, index) => (
          <div
            key={`dynamic-${index}`}
            style={{
              background: field.design.background,
              textShadow: field.design.textShadow,
            }}
            className="rounded-xl overflow-hidden shadow-lg relative"
          >
            <div className="p-6 text-white">
              <div className="flex items-center mb-3">
                <div className="mr-3 opacity-80">{field.icon}</div>
                <h3 className="text-xl font-bold tracking-wide">
                  {field.fieldName}
                </h3>
              </div>
              <div className="text-sm opacity-80 mb-2">{field.domainName}</div>
              <div className="mt-4">
                {Array.isArray(field.fieldValue) ? (
                  <ul className="space-y-2">
                    {field.fieldValue.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <span className="mr-2 mt-1">•</span>
                        <span className="font-medium break-words">
                          {typeof item === "object" && item.value
                            ? parseTextWithLinks(item.value)
                            : parseTextWithLinks(item)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="font-medium break-words">
                    {typeof field.fieldValue === "object" &&
                    field.fieldValue.value
                      ? parseTextWithLinks(field.fieldValue.value)
                      : parseTextWithLinks(field.fieldValue)}
                  </p>
                )}
              </div>
            </div>
            <div className="absolute right-6 bottom-6 opacity-70">
              <img
                src={Document}
                alt="Icon"
                className="w-28 h-28 translate-x-16 translate-y-16"
                style={{ mixBlendMode: "color-burn" }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Login screen
  if (!accessGranted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-900 to-purple-800 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-700 to-indigo-700 p-6 text-white">
            <h2 className="text-2xl font-bold mb-1">Student Profile</h2>
            <p className="opacity-80 text-sm">
              Login to access your career counseling profile
            </p>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  Email
                </label>
                <div className="relative rounded-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiMail className="text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-gray-700 text-sm font-medium mb-1"
                  htmlFor="password"
                >
                  Password
                </label>
                <div className="relative rounded-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiLock className="text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              {loginError && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{loginError}</p>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleLogin}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transform transition hover:scale-[1.02] flex items-center justify-center"
              >
                <span>Login</span>
                <FiArrowRight className="ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with nav */}
      <header className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 md:px-6 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center">
                <FiUser className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800">
                  {student?.customer?.name || "Student"}'s Profile
                </h1>
                <p className="text-xs text-gray-500">
                  {student?.customer?.email || ""}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-1 text-red-600 hover:text-red-800 px-3 py-1 border border-red-200 hover:border-red-400 rounded-md transition-colors text-sm"
            >
              <FiLogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-6 py-6 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            {/* Career Fields Card */}
            <div className="rounded-xl shadow-lg overflow-hidden">
              <div
                style={{
                  background: "linear-gradient(to right, #8368FC, #9E88FF)",
                }}
                className="p-8 relative text-white"
              >
                <h2 className="text-2xl font-semibold mb-6 flex items-center">
                  <FiStar className="mr-2" />
                  Probable Career Fields
                </h2>

                {student &&
                student?.interested_domain &&
                student.interested_domain.length > 0 ? (
                  <ul className="list-decimal list-inside space-y-2">
                    {student.interested_domain.map((domain, index) => (
                      <li
                        key={index}
                        className="text-lg font-bold tracking-wide"
                      >
                        {domain.domain_name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="italic opacity-80">
                    No career fields available
                  </p>
                )}

                <div className="absolute right-6 bottom-6">
                  <img
                    src={PurpleImage}
                    alt="Career Illustration"
                    className="w-64 h-44 object-contain"
                    style={{ mixBlendMode: "color-burn" }}
                  />
                </div>
              </div>
            </div>

            {/* Top Colleges Card */}
            <div className="rounded-xl shadow-lg overflow-hidden">
              <div
                style={{
                  background: "linear-gradient(to right, #7A9757, #88FFC5)",
                }}
                className="p-8 flex justify-between items-center text-white"
              >
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center">
                    <FiBookOpen className="mr-2" />
                    Top Colleges you can look at!
                  </h2>
                  <ul className="list-decimal list-inside space-y-2">
                    {student &&
                      student?.interested_domain?.map((domain) => (
                        <React.Fragment key={domain.id || domain.domain_name}>
                          {domain?.university_name_list?.map((uni, index) => (
                            <li
                              key={index}
                              className="text-base ml-4 font-bold tracking-wide"
                            >
                              {uni}
                            </li>
                          ))}
                        </React.Fragment>
                      ))}
                  </ul>
                </div>
                <div>
                  <img
                    src={GreenImage}
                    alt="College Illustration"
                    className="w-90 h-56 object-contain translate-x-12 translate-y-14"
                    style={{ mixBlendMode: "darken" }}
                  />
                </div>
              </div>
            </div>

            {/* Links Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {student &&
                student?.interested_domain?.map(
                  (domain, domainIndex) =>
                    domain.link_list &&
                    Object.keys(domain.link_list).length > 0 && (
                      <div
                        key={`links-${domainIndex}`}
                        style={{
                          background:
                            "linear-gradient(to right, #7F97C1, #A7BAEA)",
                        }}
                        className="rounded-xl shadow-lg overflow-hidden relative"
                      >
                        <div className="p-6">
                          <h3 className="text-lg text-left text-white font-bold mb-4 tracking-wide">
                            {domain.domain_name} Useful Links
                          </h3>

                          <ul className="space-y-3 text-white relative z-10">
                            {Object.entries(domain.link_list).map(
                              ([linkLabel, linkURL], linkIndex) => (
                                <li
                                  key={linkIndex}
                                  className="font-semibold tracking-wide"
                                >
                                  <div className="flex justify-between items-center flex-wrap gap-2">
                                    <span>{linkLabel}</span>
                                    <a
                                      href={
                                        linkURL.startsWith("http://") ||
                                        linkURL.startsWith("https://")
                                          ? linkURL
                                          : `https://${linkURL}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-2 bg-white text-blue-600 px-3 py-1 rounded hover:bg-blue-50 transition text-sm flex items-center relative z-10"
                                    >
                                      <FiExternalLink className="mr-1" />
                                      Open
                                    </a>
                                  </div>
                                  <div className="text-xs opacity-80 mt-1 truncate">
                                    {linkURL}
                                  </div>
                                </li>
                              )
                            )}
                          </ul>

                          {/* Background image with z-0 to stay behind */}
                          <div className="absolute right-6 bottom-6 opacity-70 z-0">
                            <img
                              src={pin}
                              alt="Links Icon"
                              className="w-28 h-28 translate-x-16 translate-y-16"
                              style={{ mixBlendMode: "color-burn" }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                )}

              {/* Documents Cards */}
              {student &&
                student?.interested_domain?.map(
                  (domain, domainIndex) =>
                    domain.sample_document_lists &&
                    domain.sample_document_lists.length > 0 && (
                      <div
                        key={`docs-${domainIndex}`}
                        style={{
                          background:
                            "linear-gradient(to right, #C0C5B9, #A6ABA9)",
                        }}
                        className="rounded-xl shadow-lg overflow-hidden relative"
                      >
                        <div className="p-6">
                          <h3 className="text-lg text-white font-bold mb-4 tracking-wide">
                            {domain.domain_name} Documents
                          </h3>

                          <ul className="space-y-3 text-white">
                            {domain.sample_document_lists.map(
                              (sample, index) => {
                                const fileName =
                                  typeof sample === "string"
                                    ? sample.split(
                                        "post_services_sample_documents/"
                                      )[1] || `Document ${index + 1}`
                                    : `Document ${index + 1}`;

                                return (
                                  <li
                                    key={index}
                                    className="font-semibold tracking-wide relative z-10"
                                  >
                                    <div className="flex justify-between items-center flex-wrap gap-2">
                                      <span className="truncate">
                                        {fileName}
                                      </span>
                                      <a
                                        href={`${process.env.REACT_APP_BASE_URL}/media/${sample}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-2 bg-white text-gray-600 px-3 py-1 rounded hover:bg-gray-100 transition text-sm flex items-center relative z-10"
                                      >
                                        <FiDownload className="mr-1" />
                                        Download
                                      </a>
                                    </div>
                                  </li>
                                );
                              }
                            )}
                          </ul>

                          {/* Background Image - z-0, sits behind everything */}
                          <div className="absolute right-6 bottom-6 opacity-70 z-0">
                            <img
                              src={Document}
                              alt="Documents Icon"
                              className="w-28 h-28 translate-x-16 translate-y-16"
                              style={{ mixBlendMode: "color-burn" }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                )}
            </div>

            {/* Dynamic cards */}
            {renderDynamicCards()}
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-6">
            {/* SWOT Report Card */}
            {student && student.customer && student.customer.swot_test_link && (
              <div className="bg-custom-blue text-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-6 flex flex-col justify-between h-full">
                  <div>
                    <h2 className="text-xl font-bold mb-4">
                      SWOT Analysis Report
                    </h2>
                    <p className="text-sm text-gray-300 mb-6">
                      The SWOT analysis identifies your Strengths, Weaknesses,
                      Opportunities, and Threats to help guide your career
                      development strategy.
                    </p>

                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <div className="bg-white bg-opacity-10 p-3 rounded-lg">
                        <h3 className="font-medium mb-1">Strengths</h3>
                        <p className="text-xs opacity-80">
                          What you're good at
                        </p>
                      </div>
                      <div className="bg-white bg-opacity-10 p-3 rounded-lg">
                        <h3 className="font-medium mb-1">Weaknesses</h3>
                        <p className="text-xs opacity-80">Areas to improve</p>
                      </div>
                      <div className="bg-white bg-opacity-10 p-3 rounded-lg">
                        <h3 className="font-medium mb-1">Opportunities</h3>
                        <p className="text-xs opacity-80">Chances to pursue</p>
                      </div>
                      <div className="bg-white bg-opacity-10 p-3 rounded-lg">
                        <h3 className="font-medium mb-1">Threats</h3>
                        <p className="text-xs opacity-80">
                          Challenges to address
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleDownloadSwot}
                    className="bg-white max-w-fit text-custom-blue px-8 py-2 hover:bg-gray-100 transition rounded flex items-center font-medium"
                  >
                    <FiDownload className="mr-2" />
                    Download
                  </button>
                </div>
              </div>
            )}

            {/* Contact Card */}
            <div
              className="rounded-xl shadow-lg overflow-hidden"
              style={{ background: "var(--custom-blue, #4053A5)" }} // Using the custom blue color from the second code
            >
              <div className="p-6">
                <h2 className="text-xl font-bold text-white mb-4">
                  Study Abroad
                </h2>
                <p className="text-sm text-gray-300 mb-6">
                  Interested to Study in USA, UK, Canada, Germany or in 20+
                  Countries. Get a free abroad counseling session with our
                  experts.
                </p>
                <button className="bg-white text-custom-blue px-4 py-2 rounded-md font-semibold hover:bg-gray-100 transition flex items-center">
                  <FiMessageCircle className="mr-2" />
                  Talk to our Counselor
                </button>
              </div>
            </div>

            {/* Student Info Card */}
            {student && student.customer && (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                <div className="px-6 py-4 bg-gradient-to-r from-gray-700 to-gray-600 text-white">
                  <h2 className="text-xl font-bold">Student Information</h2>
                </div>
                <div className="divide-y divide-gray-100">
                  <div className="px-6 py-4 flex justify-between">
                    <span className="text-gray-500">Name</span>
                    <span className="font-medium text-gray-900">
                      {student.customer.name}
                    </span>
                  </div>
                  <div className="px-6 py-4 flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span className="font-medium text-gray-900">
                      {student.customer.email}
                    </span>
                  </div>
                  {student.customer.phone && (
                    <div className="px-6 py-4 flex justify-between">
                      <span className="text-gray-500">Phone</span>
                      <span className="font-medium text-gray-900">
                        {student.customer.phone}
                      </span>
                    </div>
                  )}
                  {student.profile_created_at && (
                    <div className="px-6 py-4 flex justify-between">
                      <span className="text-gray-500">Profile Created</span>
                      <span className="font-medium text-gray-900">
                        {new Date(
                          student.profile_created_at
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Call to action */}
      <div className="mt-10 mb-5">
        <div
          className="py-4 px-6 flex flex-col md:flex-row justify-between items-center rounded-lg mx-4 text-white"
          style={{ background: "var(--custom-blue, #4053A5)" }}
        >
          <p className="text-sm md:text-base text-center md:text-left mb-4 md:mb-0 font-semibold tracking-wide">
            Interested to Study in USA, UK, Canada, Germany or in 20+ Countries.
            Get a free abroad counseling
          </p>
          <button className="bg-white text-custom-blue px-4 py-2 rounded-md font-semibold hover:bg-gray-100 transition">
            Talk to our Counselor
          </button>
        </div>
      </div>

      {/* Footer */}
      {/* <footer className="mt-12 py-8 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h2 className="text-xl font-bold">Career Counseling Portal</h2>
              <p className="text-gray-400 text-sm mt-1">
                Helping students reach their academic and career goals
              </p>
            </div>
            <div className="flex space-x-4">
              <button className="bg-white text-gray-900 hover:bg-gray-200 px-4 py-2 rounded transition-colors font-medium">
                Contact Support
              </button>
            </div>
          </div>
          <div className="mt-8 pt-4 border-t border-gray-800 text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Career Counseling Services. All
            rights reserved.
          </div>
        </div>
      </footer> */}
    </div>
  );
};

export default ProfileBuilding;
